import React, { useState, useEffect } from 'react';
import { Zap, Home, ClipboardList, BookOpen, BarChart2, Settings, Users, Building, Bell, Database, Sliders, LogOut, Menu, X, History, Building2, Clock, FileSpreadsheet, Radio, Briefcase, MessageCircle } from 'lucide-react';

import HomeP           from './Home';
import HourlyInput     from './HourlyInput';
import SeatMemoList    from './SeatMemoList';
import ShiftEnd        from './ShiftEnd';
import ReflectionHistory from './ReflectionHistory';
import SeatMemoHistory from './SeatMemoHistory';
import BoothAnalytics  from './BoothAnalytics';
import ErrorBoundary   from '../components/ErrorBoundary';
import AverageCheck    from './AverageCheck';
import AdminEnums      from './AdminEnums';
import AdminThresholds from './AdminThresholds';
import AdminNsSheet    from './AdminNsSheet';
import AdminLineNotify from './AdminLineNotify';
import UserManagement  from './UserManagement';
import BoothManagement from './BoothManagement';
import NotificationSettings from './NotificationSettings';
import HourlyMonitor    from './HourlyMonitor';
import LegacyKpi       from './LegacyKpi';
import CompanyManagement from './CompanyManagement';

const SIDEBAR_W = 256;
const DESKTOP_MIN = 1024;

// ボトムナビ項目（モバイル）— 日々の入力動線を優先
const BOTTOM_NAV = [
  { id: 'home',       label: 'ホーム',     Icon: Home },
  { id: 'hourly',     label: '時間別入力', Icon: Clock },
  { id: 'seat-memos', label: '着座メモ',   Icon: ClipboardList },
  { id: 'shift-end',  label: '日報',       Icon: BookOpen },
];

function navGroups(user) {
  const role = user.role;
  const groups = [
    {
      label: null, // 一番上はラベル無し
      items: [
        { id: 'home', label: 'ホーム', Icon: Home },
      ],
    },
    {
      label: '入力',
      items: [
        { id: 'hourly',     label: '時間別入力',   Icon: Clock },
        { id: 'seat-memos', label: '着座メモ',     Icon: ClipboardList },
        { id: 'shift-end',  label: '日報（稼働終了）', Icon: BookOpen },
      ],
    },
    {
      label: '分析',
      items: [
        { id: 'averages',            label: '平均確認',         Icon: BarChart2 },
        { id: 'booth-analytics',     label: 'ブース分析',       Icon: Building2 },
        { id: 'seat-memos-history',  label: '着座メモ 履歴',    Icon: History },
        { id: 'reflections-history', label: '日報 履歴',        Icon: History },
      ],
    },
  ];

  if (role === 'admin') {
    const items = [
      { id: 'hourly-monitor',   label: '時間別モニター', Icon: Radio },
      { id: 'admin-thresholds', label: '基準設定',     Icon: Sliders },
      { id: 'ns-sheet',         label: 'NSシート連携', Icon: FileSpreadsheet },
      { id: 'admin-enums',      label: '選択肢管理',   Icon: Settings },
      { id: 'users',            label: 'ユーザー管理', Icon: Users },
      { id: 'booths',           label: 'ブース管理',   Icon: Building },
      { id: 'notifications',    label: '通知設定',     Icon: Bell },
      { id: 'legacy-kpi',       label: '旧KPI履歴',    Icon: Database },
    ];
    // 会社管理は本部の管理者だけ（API側も本部adminのみ許可）。
    // ユーザーは /api/auth/login と /api/auth/me の両方で is_hq を受け取るので、
    // ログイン直後・リロード後のどちらでも正しく出し分けできる。
    if (user.is_hq) {
      items.splice(items.findIndex(i => i.id === 'users'), 0, { id: 'companies', label: '会社管理', Icon: Briefcase });
      // LINE 通知の送り先は会社をまたぐので、これも本部の管理者だけ（ADR 0030）
      items.splice(items.findIndex(i => i.id === 'ns-sheet') + 1, 0, { id: 'line-notify', label: 'LINE通知', Icon: MessageCircle });
    }
    groups.push({ label: '管理者メニュー', items });
  } else {
    groups.push({
      label: 'その他',
      items: [
        { id: 'notifications', label: '通知設定', Icon: Bell },
      ],
    });
  }

  return groups;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_MIN : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isDesktop;
}

export default function MainLayout({ user, onLogout }) {
  const [page, setPage]       = useState('home');
  const [sideOpen, setSideOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const navigate = (id) => { setPage(id); setSideOpen(false); };

  const renderPage = () => {
    switch (page) {
      case 'home':             return <HomeP user={user} onNavigate={navigate} />;
      case 'hourly':           return <HourlyInput user={user} />;
      case 'seat-memos':       return <SeatMemoList user={user} />;
      case 'shift-end':        return <ShiftEnd user={user} />;
      case 'seat-memos-history': return <SeatMemoHistory user={user} />;
      case 'reflections-history': return <ReflectionHistory user={user} onNavigate={navigate} />;
      case 'booth-analytics':  return <BoothAnalytics user={user} />;
      case 'averages':         return <AverageCheck user={user} />;
      case 'notifications':    return <NotificationSettings user={user} />;
      case 'hourly-monitor':   return user.role === 'admin' ? <HourlyMonitor /> : null;
      case 'admin-enums':      return user.role === 'admin' ? <AdminEnums /> : null;
      case 'admin-thresholds': return user.role === 'admin' ? <AdminThresholds /> : null;
      case 'ns-sheet':         return user.role === 'admin' ? <AdminNsSheet /> : null;
      case 'line-notify':      return user.role === 'admin' && user.is_hq ? <AdminLineNotify /> : null;
      case 'companies':        return user.role === 'admin' && user.is_hq ? <CompanyManagement /> : null;
      case 'users':            return user.role === 'admin' ? <UserManagement user={user} /> : null;
      case 'booths':           return user.role === 'admin' ? <BoothManagement /> : null;
      case 'legacy-kpi':       return user.role === 'admin' ? <LegacyKpi /> : null;
      default:                 return <HomeP user={user} onNavigate={navigate} />;
    }
  };

  const groups = navGroups(user);
  const sidebarVisible = isDesktop || sideOpen;

  return (
    <div style={{ height: '100%', overflow: 'hidden', background: 'var(--paper)' }}>

      {/* ── オーバーレイ（モバイルのみ） ── */}
      {!isDesktop && sideOpen && (
        <div onClick={() => setSideOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(22,32,43,0.35)', zIndex: 999 }} />
      )}

      {/* ── サイドバー ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: SIDEBAR_W,
        background: 'linear-gradient(180deg,#080E1A 0%,#0B1020 40%,#151038 100%)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid var(--rule)',
        boxShadow: isDesktop ? 'none' : 'var(--shadow-pop)',
        display: 'flex', flexDirection: 'column',
        transform: sidebarVisible ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 240ms cubic-bezier(.4,0,.2,1)',
        zIndex: 1000,
      }}>
        {/* ロゴ */}
        <div style={{ padding: 'var(--s6) var(--s4) var(--s4)', borderBottom: '1px solid var(--rule)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)', marginBottom: 'var(--s3)' }}>
            <span style={{
              width: 32, height: 32, borderRadius: 'var(--r-sm)', background: 'var(--grad-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
            }}>
              <Zap size={17} color="#fff" />
            </span>
            <span style={{
              fontSize: 'var(--t-lead)', fontWeight: 800, letterSpacing: '0.02em',
              background: 'linear-gradient(135deg,#a5b4fc,#c4b5fd)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>催事KPI</span>
          </div>
          <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink)' }}>{user.name}</div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 2 }}>
            {user.role === 'admin' ? '管理者' : 'スタッフ'}
          </div>
        </div>

        {/* ナビ（グループ別） */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
          {groups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: gi === groups.length - 1 ? 0 : 12 }}>
              {group.label && (
                <div style={{
                  padding: 'var(--s3) var(--s3) var(--s1)',
                  fontSize: 'var(--t-note)',
                  fontWeight: 600,
                  color: 'var(--ink-mute)',
                }}>
                  {group.label}
                </div>
              )}
              {group.items.map(({ id, label, Icon }) => {
                const active = page === id;
                return (
                  <button key={id} onClick={() => navigate(id)} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '0 var(--s3)', borderRadius: 'var(--r-sm)', border: 'none', marginBottom: 2,
                    background: active ? 'rgba(99,102,241,0.18)' : 'transparent',
                    color: active ? 'var(--mark)' : 'var(--ink-soft)',
                    fontWeight: active ? 700 : 500, fontSize: 'var(--t-body)',
                    cursor: 'pointer', textAlign: 'left', transition: 'background-color 120ms, color 120ms',
                    minHeight: 44,
                  }}>
                    <Icon size={18} />
                    {label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* ログアウト */}
        <div style={{ padding: 'var(--s2)', borderTop: '1px solid var(--rule)' }}>
          <button onClick={() => { if (window.confirm('ログアウトしますか？')) onLogout(); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--s2)', padding: '0 var(--s3)', borderRadius: 'var(--r-sm)', border: 'none', background: 'transparent', color: 'var(--ink-mute)', fontSize: 'var(--t-body)', cursor: 'pointer', minHeight: 44 }}>
            <LogOut size={18} /> ログアウト
          </button>
        </div>
      </nav>

      {/* ── モバイルヘッダー（モバイルのみ） ── */}
      {!isDesktop && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 800,
          display: 'flex', alignItems: 'center', gap: 10,
          paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
          paddingBottom: 8, paddingLeft: 12, paddingRight: 12,
          background: 'var(--field)',
          borderBottom: '1px solid var(--rule)',
        }}>
          <button onClick={() => setSideOpen(v => !v)} aria-label="メニュー" style={{
            background: 'transparent', border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            flexShrink: 0,
          }}>
            {sideOpen ? <X size={22} color="var(--ink)" /> : <Menu size={22} color="var(--ink)" />}
          </button>
          <span style={{
            fontWeight: 800, fontSize: 'var(--t-lead)',
            background: 'linear-gradient(135deg,#a5b4fc,#c4b5fd)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>催事KPI</span>
          <span style={{ marginLeft: 'auto', fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{user.name}</span>
        </div>
      )}

      {/* ── コンテンツ ── */}
      {/* スクロールするのはここだけ。ドキュメントを動かさないことで、
          画面を上にスライドしたときにボトムナビが浮き上がるのを防ぐ。 */}
      <main style={{
        paddingTop: isDesktop ? 24 : 'calc(60px + env(safe-area-inset-top, 0px))',
        paddingBottom: isDesktop ? 32 : 'calc(72px + env(safe-area-inset-bottom, 0px))',
        paddingLeft: isDesktop ? SIDEBAR_W : 0,
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain',
        transition: 'padding-left 240ms cubic-bezier(.4,0,.2,1)',
      }}>
        {/* 切替アニメは opacity だけ（transform を含む keyframes だと、この要素が
            position:fixed の子孫の基準の箱になり、全画面モーダルが縮んでしまう）。 */}
        <div key={page} style={{ animation: 'pageIn 200ms ease-out' }}>
          <ErrorBoundary key={page}>
            {renderPage()}
          </ErrorBoundary>
        </div>
      </main>

      {/* ── ボトムナビ（モバイルのみ） ── */}
      {!isDesktop && (
        <nav className="app-bottom-nav" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 800,
          background: 'var(--field)',
          borderTop: '1px solid var(--rule)',
          display: 'flex', alignItems: 'stretch',
          padding: '4px 4px',
          paddingBottom: 'calc(4px + env(safe-area-inset-bottom, 0px))',
        }}>
          {BOTTOM_NAV.map(({ id, label, Icon }) => {
            const active = page === id;
            return (
              <button key={id} onClick={() => navigate(id)} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, padding: '6px 2px', borderRadius: 'var(--r-sm)', border: 'none',
                background: active ? 'var(--mark-soft)' : 'transparent',
                color: active ? 'var(--mark)' : 'var(--ink-mute)',
                cursor: 'pointer', transition: 'all 120ms',
                minHeight: 56,
              }}>
                <Icon size={22} strokeWidth={active ? 2.4 : 2} />
                <span style={{ fontSize: 'var(--t-note)', fontWeight: active ? 700 : 600 }}>{label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
